import { Grid, Theme } from "@mui/material";
import { createStyles, withStyles, WithStyles } from "@mui/styles";
import * as api from "api/Knowledge";
import { FeedStyleClasses, TimelineStyleClasses } from "assets/jss/common-styles";
import { RequestIndicator } from "components/CallServer";
import WidgetDrawer from "components/WidgetDrawer";
import { deepMerge } from "lib/DeepMerge";
import { FeedType } from "models/ApiEnums";
import { FeedItemDto } from "models/ApiModels";
import * as React from "react";
import ReactResizeDetector from "react-resize-detector";
import { RouteComponentProps } from "react-router";
import { FeedPostWindowMessages } from "store/feed/windowMessages";
import { SidebarPostWindowMessages } from "store/sidebar/windowMessages";
import { CheckPublicationDataIsOkToContinue } from "views/Publication/PostViewer";
import WidgetRelations from "views/Publication/Widgets/WidgetRelations";
import WidgetStatResume from "views/Publication/Widgets/WidgetStatResume";
import QuestionCard from "views/Timeline/Components/CardFactory/QuestionCard";
import FeedAnswers, { FeedAnswersComponent } from "views/Timeline/Components/FeedAnswers";

/////////////////////////////////////////////////////////////////////////////////////

interface IState {
    loading: boolean;
    updating: boolean;
    deleting: boolean;
    mouseOver: boolean;
    dialog: any;
    data: FeedItemDto;
    orderByMenuAnchorEl: any;
}

interface IProps {
}

interface IRouteParams {
    id: string;
    aid: string;
}

const styles = (theme: Theme) => createStyles({
    ...TimelineStyleClasses(theme),
    ...FeedStyleClasses(theme)
});

type Props = IProps & RouteComponentProps<IRouteParams> & WithStyles<typeof styles>;

/////////////////////////////////////////////////////////////////////////////////////

class QuestionViewer extends React.PureComponent<Props, IState> {

    private feedRef: FeedAnswersComponent;
    private feedWindowMessages = new FeedPostWindowMessages(window.opener);
    private sidebarWindowMessages = new SidebarPostWindowMessages(window.opener);

    constructor(props: Props) {
        super(props);
        this.state = {
            data: null,
            dialog: null,
            orderByMenuAnchorEl: null,
            mouseOver: false,
            deleting: false,
            updating: false,
            loading: true,
        };
    }

    //#region QUESTION

    ///
    private loadQuestion = () => {
        return api.getFeedItemPublication(this.props.match.params.id, true)
            .then((data) => {
                if (data.bestAnswer == null) data.body.fullContentLoaded = null;
                this.setState({ data, loading: false });
            })
            .catch(() => {
                this.setState({ loading: false });
            });
    }

    ///
    private handleInfo = () => {
        this.setState({
            dialog: (
                <WidgetDrawer onClose={() => this.setState({ dialog: null })} >
                    <WidgetStatResume data={this.state.data} />
                </WidgetDrawer>
            )
        });
    };

    ///
    private handleDeleteQuestion = (): Promise<boolean> => {
        const { publicationId, publicationType } = this.state.data;

        return new Promise<boolean>((resolve, reject) => {

            api.deletePublication(publicationId, publicationType)
                .then((result) => {
                    if (result) {
                        this.feedWindowMessages.tryDeleteFeedData(publicationId);
                        window.close();
                    }
                    resolve(result);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    };

    ///
    private handleUpdateQuestion = (newData: DeepPartial<FeedItemDto>) => {
        this.setState({ data: deepMerge(this.state.data, newData, { arrayMerge: (target: any, source: any, options: any) => source }) });
        this.feedWindowMessages.tryUpdateFeedData(newData);
    };

    ///
    private handleExpandQuestion = (textExpanded: boolean): Promise<void> => {

        const { publicationId } = this.state.data;

        return new Promise<void>((resolve) => {

            const d = { ...this.state.data };

            if (textExpanded) {

                api.getPublicationFullContent(publicationId)
                    .then((fullContent) => {

                        if (fullContent) {
                            d.body.fullContentLoaded = textExpanded;
                            d.body.fullContent = fullContent;
                        }
                        this.setState({ data: d });
                        resolve();
                    })
                    .catch(() => {
                        resolve();
                    });
            }
            else {
                d.body.fullContentLoaded = textExpanded;

                this.setState({ data: d });
                resolve();
            }
        });
    };

    //#endregion


    //#region ANSWER

    ///
    private handleAddAnswer = (publicationId: string): Promise<void> => {
        return this.feedRef.addItemToFeed(publicationId);
    };

    //#endregion 


    //#region RENDERS

    ///
    private renderQuestion(width: number) {
        const { data } = this.state;
        const { classes } = this.props;

        return (
            <>
                <QuestionCard
                    classes={classes}
                    item={data}
                    showRelationsButton={width < 920}
                    onInfoRequest={width < 920 ? this.handleInfo : null}
                    feedType={FeedType.Any}
                    onAddCard={this.handleAddAnswer}
                    onUpdateCard={this.handleUpdateQuestion}
                    onExpandCardContent={this.handleExpandQuestion}
                    onRemovePublication={this.handleDeleteQuestion}
                    onReloadPublication={this.loadQuestion}
                    onSidebarResetRequest={
                        () => {
                            this.sidebarWindowMessages.reset();
                        }
                    }

                />

                <FeedAnswers
                    onRef={
                        (ref: FeedAnswersComponent) => {
                            this.feedRef = ref;
                        }
                    }
                    questionId={this.props.match.params.id}
                    goAnswerId={this.props.match.params.aid}
                    classes={classes}
                    canAdd={true}
                    totalAnswers={data.statistics.answers}
                />

            </>
        );
    }


    //#endregion


    ///
    public componentDidMount() {
        this.loadQuestion();
    }

    ///
    public render() {
        const { classes } = this.props;
        const { data, loading } = this.state;

        if (loading)
            return <RequestIndicator type="request" />;

        var noContinueMessage = CheckPublicationDataIsOkToContinue(data);
        if (noContinueMessage != null) return <div className={classes.timelineContainer}>{noContinueMessage}</div>;

        return (
            <>
                {this.state.dialog}

                <ReactResizeDetector handleWidth refreshMode="debounce" refreshRate={0}>
                    {
                        (d: ReactResizeDetectorDimensions) =>

                            <Grid container spacing={1} direction="row" style={{ flexWrap: "nowrap" }}>
                                <Grid item xs>

                                    <div className={classes.timelineContainer}>
                                        {this.renderQuestion(d.width)}
                                    </div>
                                </Grid>

                                {
                                    d.width > 919 &&
                                    <Grid item style={{ width: 350 }}>
                                        {
                                            data.publication.hasRelations &&
                                            <WidgetRelations publicationId={data.publicationId} />
                                        }
                                        <WidgetStatResume data={data} />
                                    </Grid>
                                }

                            </Grid>
                    }
                </ReactResizeDetector>
            </>
        );
    }
}

export default withStyles(styles)(QuestionViewer);